import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLeoChatStore, type TLeoChatMessage } from "@/zapi-frontend/stores/leoChatStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { MODEL_LEDGER_QUERY_KEY } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ModelLedgerIsland/useModelLedger";
import { RQA_SIM_QUERY_KEY } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/RqaSimIsland/useRqaSim";
import { RISKS_QUERY_KEY } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/RisksIsland/useRisks";
import { getActiveLeoChatTask, getLeoChatHistory, getLeoChatTaskStatus, type TLeoChatProgress } from "../api/leoChat";
import { followedTask, historyMessages } from "../lib/leoHistory";
import { followDeckRefresh, reloadPresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { HOME_SLUG, OUTLINE_SLUG, leoEditedHome, leoStatus, type TLeoStatus } from "../lib/leoProgress";

const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;
// The runner allows hours-long tasks; this only frees the composer when the runner is
// unreachable for reconciliation. Sized well above real runs (multi-chapter edit ≈ 4 min on
// sonnet) with room for the revision to be on opus.
const MAX_TASK_MS = 45 * 60 * 1000;
// An edit can move the Model Ledger's figures, and RQASim is solved from them.
const ANALYSIS_QUERY_KEYS = [MODEL_LEDGER_QUERY_KEY, RQA_SIM_QUERY_KEY, RISKS_QUERY_KEY];
const FOLLOW_UP_WATCH_MS = 3 * 60 * 1000;

type TFollowUpWatch = { after: string; until: number };

const chapterTitleOf = (slug: string): string | undefined => {
  const chapter = useChapterStore
    .getState()
    .projectChapters.find(({ projectTemplateChapter }) => projectTemplateChapter.slug === slug)?.projectTemplateChapter;
  return chapter ? chapter.nav || chapter.name : undefined;
};

const statusOf = (progress: TLeoChatProgress | null | undefined): TLeoStatus => leoStatus(progress, chapterTitleOf);

const isChapterSlug = (slug: string) => chapterTitleOf(slug) !== undefined;

// The transcript names every chapter file an edit tool wrote. Only a backend that does not report
// it falls back to the live status, which also reads a shell command that names a chapter as an edit.
const editedChaptersOf = (progress: TLeoChatProgress | null | undefined, status: TLeoStatus): string[] =>
  progress?.editedFiles
    ? progress.editedFiles.map((file) => file.replace(/\.(md|json)$/, "")).filter(isChapterSlug)
    : status.kind === "editing" && isChapterSlug(status.slug)
    ? [status.slug]
    : [];

const refreshEditedChapters = async (slugs: string[]) => {
  if (slugs.length === 0) return;
  const { projectChapters, syncChapterContent, invalidateChapterCroppedImages } = useChapterStore.getState();
  const chapterIds = slugs
    .map((slug) => projectChapters.find((chapter) => chapter.projectTemplateChapter.slug === slug)?.id)
    .filter((id): id is number => id != null);
  await Promise.all(
    chapterIds.map(async (id) => {
      const chapterId = id.toString();
      await syncChapterContent(chapterId).catch(() => undefined);
      await invalidateChapterCroppedImages(chapterId).catch(() => undefined);
    })
  );
};

// Follows the project's running Leo turn for the whole project page rather than for the chat
// panel, which some views do not mount: the page, the sidebar and the chat all read what this
// writes to the store, and the edited chapters are refreshed wherever the reader is.
export function useLeoTaskPolling(projectId: string) {
  const queryClient = useQueryClient();
  const activeTaskId = useLeoChatStore((s) => s.activeTaskByProject[projectId]?.id ?? null);
  const addMessage = useLeoChatStore((s) => s.addMessage);
  const setActiveTask = useLeoChatStore((s) => s.setActiveTask);
  const trackTaskActivity = useLeoChatStore((s) => s.trackTaskActivity);
  const startRefresh = useLeoChatStore((s) => s.startRefresh);
  const endRefresh = useLeoChatStore((s) => s.endRefresh);
  const setHistory = useLeoChatStore((s) => s.setHistory);
  const isStarting = useLeoChatStore((s) => !!s.startingByProject[projectId]);
  const [watch, setWatch] = useState<TFollowUpWatch | null>(null);

  // One chat per project, so after a reload the page picks up the latest unfinished turn.
  useEffect(() => {
    if (!projectId || useLeoChatStore.getState().activeTaskByProject[projectId]) return;
    let cancelled = false;
    getActiveLeoChatTask(projectId)
      .then((task) => {
        if (cancelled || !task.id) return;
        if (useLeoChatStore.getState().activeTaskByProject[projectId]) return;
        setActiveTask(projectId, followedTask({ ...task, id: task.id }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, setActiveTask]);

  useEffect(() => {
    if (!projectId || !activeTaskId) return;
    setWatch(null);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let errors = 0;

    const finishWithMessage = (message: TLeoChatMessage) => {
      addMessage(projectId, message);
      setActiveTask(projectId, null);
    };

    // The task is over before its changes reach the screen; the page keeps showing that the
    // analysis is being updated until everything they touched has been fetched again. "home" and
    // "slides" are not chapters: the Home page comes with the project, the outline on its own.
    const loadAppliedChanges = async (taskId: string, appliedChapters: string[]) => {
      startRefresh(projectId, taskId);
      void followDeckRefresh(projectId);
      try {
        await Promise.allSettled([
          refreshEditedChapters(appliedChapters.filter((slug) => slug !== HOME_SLUG && slug !== OUTLINE_SLUG)),
          appliedChapters.includes(HOME_SLUG) ? useProjectStore.getState().fetchSelectedProject(projectId, true) : null,
          appliedChapters.includes(OUTLINE_SLUG) ? reloadPresentation(projectId) : null,
          ...ANALYSIS_QUERY_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key, projectId] }))
        ]);
      } finally {
        endRefresh(projectId, taskId);
      }
    };

    const poll = async () => {
      if (cancelled) return;
      const active = useLeoChatStore.getState().activeTaskByProject[projectId];
      if (active?.startedAt && Date.now() - active.startedAt > MAX_TASK_MS) {
        finishWithMessage({
          role: "assistant",
          content:
            "This is taking longer than expected. The task keeps running in the background; edits will apply when it finishes."
        });
        return;
      }
      let state;
      try {
        state = await getLeoChatTaskStatus(activeTaskId);
      } catch {
        if (cancelled) return;
        errors += 1;
        if (errors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          finishWithMessage({
            role: "assistant",
            content: "Lost connection to the chat task. Please try again.",
            isError: true
          });
          return;
        }
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        return;
      }
      if (cancelled) return;
      errors = 0;

      if (state.status === "succeeded") {
        const appliedChapters = state.appliedChapters ?? [];
        const followsUp = useLeoChatStore.getState().activeTaskByProject[projectId]?.canEdit ?? false;
        if (appliedChapters.length > 0) void loadAppliedChanges(activeTaskId, appliedChapters);
        // A turn that edits only the deck's pages lists no pages, but the deck is re-printed all the same.
        else if (followsUp) void followDeckRefresh(projectId);
        finishWithMessage({
          role: "assistant",
          content: state.reply || "(The task finished without a reply.)",
          refs: state.reply ? state.replyRefs : null,
          appliedChapters,
          ...(state.appliedSlides?.length ? { appliedSlides: state.appliedSlides } : {}),
          estCostUsd: state.estCostUsd ?? null
        });
        if (followsUp) setWatch({ after: activeTaskId, until: Date.now() + FOLLOW_UP_WATCH_MS });
        return;
      }
      if (state.status === "cancelled") {
        finishWithMessage({ role: "assistant", content: "Stopped.", isError: true });
        return;
      }
      if (state.status === "failed") {
        finishWithMessage({
          role: "assistant",
          content: state.error || "Leo could not finish this request. Please try again.",
          isError: true
        });
        return;
      }
      const nextStatus = statusOf(state.progress);
      trackTaskActivity(projectId, activeTaskId, {
        liveStatus: nextStatus,
        editedChapters: editedChaptersOf(state.progress, nextStatus),
        editedHome: leoEditedHome(state.progress, nextStatus)
      });
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, activeTaskId, queryClient, addMessage, setActiveTask, trackTaskActivity, startRefresh, endRefresh]);

  useEffect(() => {
    // While a message is being sent the server's history does not have it yet; syncing would drop it.
    if (!projectId || !watch || activeTaskId || isStarting) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const syncHistory = async (always: boolean) => {
      const items = await getLeoChatHistory(projectId).catch(() => null);
      if (cancelled || !items || useLeoChatStore.getState().startingByProject[projectId]) return;
      const server = historyMessages(items);
      const local = useLeoChatStore.getState().historyByProject[projectId] ?? [];
      if (always || server[server.length - 1]?.content !== local[local.length - 1]?.content)
        setHistory(projectId, server);
    };
    const check = async () => {
      if (Date.now() >= watch.until) {
        await syncHistory(false);
        if (!cancelled) setWatch(null);
        return;
      }
      const task = await getActiveLeoChatTask(projectId).catch(() => null);
      if (cancelled || useLeoChatStore.getState().activeTaskByProject[projectId]) return;
      if (task?.id && task.id !== watch.after) {
        await syncHistory(true);
        if (cancelled || useLeoChatStore.getState().activeTaskByProject[projectId]) return;
        setActiveTask(projectId, followedTask({ ...task, id: task.id }));
        return;
      }
      await syncHistory(false);
      if (cancelled) return;
      timer = setTimeout(() => void check(), POLL_INTERVAL_MS);
    };
    timer = setTimeout(() => void check(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, watch, activeTaskId, isStarting, setActiveTask, setHistory]);
}
